<template>
  <div>
    <v-data-table
      :headers="headers"
      :options.sync="tableDataGetter.pagination"
      :server-items-length="tableDataGetter.pagination.totalItems"
      :loading="tableDataStatusPending"
      :items="tableDataGetter.allList.data"
      :footer-props="{
        itemsPerPageOptions: tableDataGetter.pagination.rowsPerPageItems,
        itemsPerPageText: $t('general.rowsPerPage'),
      }"
      :item-key="tableSettings.itemKey || tableData.itemKey || '_id'"
      @click:row="$emit('clickedRow', $event)"
      @update:options="getData()"
      v-model="selectedItems"
      :show-select="canSelect"
      :class="{ 'pointer-cursor': hasClickedRowListener }"
    >
      <template v-slot:top v-if="tableSettings.showHeaderToolbar">
        <v-toolbar flat color="white">
          <template v-if="isSelecting">
            <v-btn icon @click="clearSelectItems">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title class="body-1 font-weight-medium">
              {{ $t("general.selectedCount", { count: selectedItems.length }) }}
            </v-toolbar-title>
            <div class="flex-grow-1"></div>
            <div
              v-for="item in tableSettings.multipleSelectButtons"
              v-bind:key="item.title"
              v-show="buttonAllowed(item, true)"
            >
              <v-btn
                :color="item.color"
                :dark="item.dark"
                :outlined="item.outlined"
                class="mb-2 mr-4"
                @click="$emit(item.action, selectedItems)"
                >{{ $t(item.title) }}
                <v-icon right :dark="item.iconDark" v-if="item.icon"
                  >{{ item.icon }}
                </v-icon></v-btn
              >
            </div>
          </template>
          <template v-else>
            <v-toolbar-title v-if="tableSettings.title != ''">{{
              tableSettings.title
            }}</v-toolbar-title>
            <v-divider
              v-if="tableSettings.title != ''"
              class="mx-4"
              inset
              vertical
            ></v-divider>
            <v-text-field
              style="max-width: 370px"
              v-model="tableDataGetter.pagination.search"
              :label="$t('general.search')"
              id="search-all"
              single-line
              hide-details
              dense
              clearable
              @click:clear="handleClickClear()"
              @input="handleSearchInput"
              @keyup.enter.native="getData()"
            ></v-text-field>
            <v-btn text icon color="secondary" @click="getData()">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
            <v-btn
              v-if="anyFilterActive"
              outlined
              small
              color="primary"
              class="ml-3"
              @click="clearAllColumnFilters"
            >
              <v-icon left small>mdi-filter-variant</v-icon>
              {{ $t("general.resetFilters") }}
            </v-btn>
            <div class="flex-grow-1"></div>
            <div
              v-for="item in tableSettings.headerButtons"
              v-bind:key="item.title"
              v-show="buttonAllowed(item, false)"
            >
              <v-btn
                :text="item.text"
                :color="item.color"
                :dark="item.dark"
                :outlined="item.outlined"
                :disabled="item.disabled"
                class="mb-2 mr-4"
                @click="$emit(item.action, $event)"
                >{{ $t(item.title) }}
                <v-icon right :dark="item.iconDark" v-if="item.icon"
                  >{{ item.icon }}
                </v-icon></v-btn
              >
            </div>
          </template>
        </v-toolbar>
      </template>
      <template
        v-for="fh in filterableHeaders"
        v-slot:[`header.${fh.value}`]="{ header }"
      >
        <span :key="fh.value" class="dt-filter-header">
          <v-menu offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <span class="dt-filter-activator" v-on="on" @click.stop>
                <span>{{ header.text }}</span>
                <v-badge
                  dot
                  color="primary"
                  overlap
                  class="dt-filter-badge"
                  offset-x="4"
                  offset-y="8"
                  :value="isFilterActive(fh)"
                >
                  <v-icon
                    small
                    class="ml-1 dt-filter-icon"
                    :class="{ 'dt-filter-icon--active': isFilterActive(fh) }"
                    :color="isFilterActive(fh) ? 'primary' : ''"
                  >
                    mdi-filter-variant
                  </v-icon>
                </v-badge>
              </span>
            </template>
            <v-card min-width="260" class="pa-3">
              <template v-for="def in fh.filters">
                <v-autocomplete
                  v-if="def.searchable"
                  :key="def.param"
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-details
                  clearable
                  class="mb-2"
                  :items="def.items"
                  :label="def.label || header.text"
                  v-model="columnFilters[def.param]"
                  @change="applyFilters"
                />
                <v-select
                  v-else
                  :key="def.param"
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-details
                  clearable
                  class="mb-2"
                  :items="def.items"
                  :label="def.label || header.text"
                  v-model="columnFilters[def.param]"
                  @change="applyFilters"
                >
                  <template v-if="def.image" v-slot:selection="{ item }">
                    <v-chip small>
                      <img :src="item.value" height="18" class="mr-1" />
                    </v-chip>
                  </template>
                  <template
                    v-if="def.image"
                    v-slot:item="{ item, on, attrs }"
                  >
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-action>
                        <v-simple-checkbox
                          :value="attrs.inputValue"
                          @click="on.click"
                        />
                      </v-list-item-action>
                      <img :src="item.value" height="26" />
                    </v-list-item>
                  </template>
                </v-select>
              </template>
            </v-card>
          </v-menu>
        </span>
      </template>
      <!-- render slot props and components from prop data -->
      <template
        v-for="slot in tableData.tableComponentSlots"
        v-slot:[slot.slotName]="{ item }"
      >
        <component
          :is="slot.componentName"
          :key="slot.id"
          :item="item"
          :config="slot.config"
          @reloadData="getData()"
        />
      </template>
      <template v-slot:no-data> {{ $t("general.noData") }} </template>
    </v-data-table>
  </div>
</template>
<script>
import * as components from "../../../../src/components/tableSlotComponents/index.js";
import { mapGetters } from "vuex";
import { tableActionAllowed } from "../../../utils/access";

const READ_ACTIONS = ["exportWithTimespan", "openExport"];
export default {
  components: {
    ...components,
  },
  props: ["tableSettings", "tableData", "headers"],
  data: () => ({
    selectedItems: [],
    columnFilters: {},
  }),
  computed: {
    filterableHeaders() {
      return (this.headers || []).filter(
        (h) => Array.isArray(h.filters) && h.filters.length > 0,
      );
    },
    anyFilterActive() {
      return this.filterableHeaders.some((fh) => this.isFilterActive(fh));
    },
    filterQuery() {
      const usp = new URLSearchParams();
      Object.keys(this.columnFilters).forEach((param) => {
        (this.columnFilters[param] || []).forEach((v) => {
          usp.append(param, v === true ? "true" : v === false ? "false" : v);
        });
      });
      const s = usp.toString();
      return s ? "&" + s : "";
    },
    canSelect() {
      if (!this.tableSettings.showSelect || this.isReadOnlyUser) return false;
      const buttons = this.tableSettings.multipleSelectButtons;
      if (!buttons || !buttons.length) return true;
      return buttons.some((item) => this.buttonAllowed(item, true));
    },
    isSelecting() {
      return (
        this.selectedItems.length > 0 &&
        this.tableSettings.multipleSelectButtons &&
        this.tableSettings.multipleSelectButtons.length > 0
      );
    },
    hasClickedRowListener() {
      return this.$listeners && this.$listeners.clickedRow;
    },
    pagination: {
      get: function () {
        return this.$store.getters.tableDataGetter.pagination;
      },
      set: function (value) {
        this.$store.commit("SET_PAGINATION_TABLE", value);
        //this.getData();
      },
    },
    ...mapGetters(["tableDataGetter", "tableDataStatusPending"]),
  },
  watch: {
    "tableDataGetter.allList.data": function (newData) {
      this.$emit("dataChanged", newData);
    },
    filterableHeaders: {
      immediate: true,
      handler(headers) {
        headers.forEach((h) => {
          h.filters.forEach((def) => {
            if (!(def.param in this.columnFilters)) {
              this.$set(this.columnFilters, def.param, []);
            }
          });
        });
      },
    },
  },
  mounted() {},
  methods: {
    buttonAllowed(item, selection) {
      if (item.readOnlySafe || READ_ACTIONS.includes(item.action)) return true;
      if (this.isReadOnlyUser) return false;
      if (item.structure) return this.canManageStructureHere;
      return tableActionAllowed(this.currentModule, item.action, selection);
    },
    clearSelectItems() {
      this.selectedItems = [];
    },
    getData() {
      const td = this.filterQuery
        ? {
            ...this.tableData,
            uriPartAppend: (this.tableData.uriPartAppend || "") + this.filterQuery,
          }
        : this.tableData;
      this.$store.dispatch("getTableData", td);
    },
    applyFilters() {
      this.tableDataGetter.pagination.page = 1;
      this.getData();
    },
    clearAllColumnFilters() {
      Object.keys(this.columnFilters).forEach((param) => {
        this.columnFilters[param] = [];
      });
      this.applyFilters();
    },
    isFilterActive(fh) {
      return fh.filters.some((def) => {
        const vals = this.columnFilters[def.param];
        return vals && vals.length > 0;
      });
    },
    handleClickClear() {
      this.tableDataGetter.pagination.search = "";
      this.getData();
    },
    //emptying the field has to reload the full list, otherwise the last search
    //result stays on screen until the user presses enter again
    handleSearchInput(value) {
      if (value === "" || value === null) {
        this.getData();
      }
    },
  },
};
</script>
<style>
.v-data-table__expanded__content {
  box-shadow: none !important;
  background: white !important;
}

.pointer-cursor tr {
  cursor: pointer;
}

.dt-filter-header {
  display: inline-flex;
  align-items: center;
}
.dt-filter-activator {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}
/* Columns with a filter open the filter on click, so the sort arrow is hidden.
   Sorting itself is untouched - remove this rule to bring the arrows back. */
.dt-filter-header + .v-data-table-header__icon,
.dt-filter-header ~ .v-data-table-header__icon {
  display: none !important;
}
.dt-filter-icon {
  opacity: 0.55;
}
.dt-filter-badge .v-badge__badge {
  min-width: 7px !important;
  width: 7px !important;
  height: 7px !important;
}
.dt-filter-icon--active {
  opacity: 1;
}
.dt-filter-icon:hover {
  opacity: 1;
}

.v-data-table .v-data-table-header th {
  vertical-align: middle;
  white-space: nowrap;
}
.v-data-table .v-data-table-header th > span,
.v-data-table .v-data-table-header th .v-data-table-header__content,
.v-data-table .v-data-table-header th .v-data-table-header__icon {
  vertical-align: middle;
}
.v-data-table .v-data-table-header th.sortable .v-data-table-header__icon {
  opacity: 0.38;
}
.v-data-table
  .v-data-table-header
  th.sortable:hover
  .v-data-table-header__icon,
.v-data-table
  .v-data-table-header
  th.sortable.active
  .v-data-table-header__icon {
  opacity: 1;
}
</style>
<!--
Data models:
-->
